import React, { useState } from "react";
import { Label, Textarea, Dropdown, Option, makeStyles } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import SearchResourceInGraph from "../../GenericComponent/SearchResourceInGraph";
import QueueSelectorComponent from "../../GenericComponent/QueueSelectorComponent";
import "./CSS/CheckVariableComponent.css";

const useStyles = makeStyles({
  container: { display: "flex", flexDirection: "column", gap: "1rem" },
  input: { padding: "4px 8px", borderRadius: "4px", border: "none", width: "120px", backgroundColor: "#f3f2f1" },
});

const ACTIONS = [
  { value: "queue", textKey: "queue" },
  { value: "transferUser", textKey: "transferToUser" },
  { value: "disconnect", textKey: "disconnect" },
  { value: "routing", textKey: "routing" },
  { value: "transferPhone", textKey: "transferToPhone" },
];

export default function CheckVariableComponent({ data, routingName, onChange, routingList, permissions, isEditMode }) {

  const { t } = useTranslation();
  const classes = useStyles();

  const [matchedAction, setMatchedAction] = useState(data.onMatched?.type || "");
  const [unmatchedAction, setUnmatchedAction] = useState(data.onUnMatched?.type || "");

  const [selectedMatchedQueue, setSelectedMatchedQueue] = useState({
    displayName: data.onMatched?.queueDisplayName || "",
    id: data.onMatched?.queueId || "",
  });
  const [selectedMatchedUser, setSelectedMatchedUser] = useState({
    displayName: data.onMatched?.userDisplayName || "",
    id: data.onMatched?.targetId || "",
  });
  const [selectedMatchedRouting, setSelectedMatchedRouting] = useState(data.onMatched?.routingName || "");
  const [matchedPhone, setMatchedPhone] = useState(
    data.onMatched?.type === "transfer" && data.onMatched?.targetType === "phone" ? data.onMatched?.targetId || "" : ""
  );

  const [selectedUnmatchedQueue, setSelectedUnmatchedQueue] = useState({
    displayName: data.onUnMatched?.queueDisplayName || "",
    id: data.onUnMatched?.queueId || "",
  });
  const [selectedUnmatchedUser, setSelectedUnmatchedUser] = useState({
    displayName: data.onUnMatched?.userDisplayName || "",
    id: data.onUnMatched?.targetId || "",
  });
  const [selectedUnmatchedRouting, setSelectedUnmatchedRouting] = useState(data.onUnMatched?.routingName || "");
  const [unmatchedPhone, setUnmatchedPhone] = useState(
    data.onUnMatched?.type === "transfer" && data.onUnMatched?.targetType === "phone" ? data.onUnMatched?.targetId || "" : ""
  );

  // Utilitaire pour déterminer la valeur du Dropdown
  const getDropdownValue = (action, phone, user, type, dataObj) => {
    if (action === "transfer") {
      if (dataObj?.targetType === "phone" || phone) return "transferPhone";
      if (dataObj?.targetType === "user") return "transferUser";
      return "transferUser";
    }
    return action;
  };

  const getDropdownDisplayValue = (action, phone, user, type, dataObj) => {
    const value = getDropdownValue(action, phone, user, type, dataObj);
    return t(ACTIONS.find(a => a.value === value)?.textKey || value);
  };

  const handleActionChange = (value, type) => {
    const isMatched = type === "matched";

    if (isMatched) {
      setSelectedMatchedQueue({ displayName: "", id: "" });
      setSelectedMatchedUser({ displayName: "", id: "" });
      setSelectedMatchedRouting("");
      setMatchedPhone("");
      const actionType = (value === "transferUser" || value === "transferPhone") ? "transfer" : value;
      setMatchedAction(actionType);
    } else {
      setSelectedUnmatchedQueue({ displayName: "", id: "" });
      setSelectedUnmatchedUser({ displayName: "", id: "" });
      setSelectedUnmatchedRouting("");
      setUnmatchedPhone("");
      const actionType = (value === "transferUser" || value === "transferPhone") ? "transfer" : value;
      setUnmatchedAction(actionType);
    }

    let obj = {};
    
    if (value === "transferUser") {
      obj = { type: "transfer", targetType: "user" };
    } else if (value === "transferPhone") {
      obj = { type: "transfer", targetType: "phone" };
    } else {
      obj = { type: value };
    }
    
    onChange(routingName, isMatched ? "onMatched" : "onUnMatched", obj);
  };

  return (
    <div className={classes.container}>
      <Label>{t("formula")}</Label>
      <Textarea
        className="check-variable-textarea"
        resize="both"
        value={data?.formula || ""}
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        onChange={(e, val) => onChange(routingName, "formula", val.value)}
      />

      {/* Matched */}
      <Label>{t("ifMatched")}</Label>
      <Dropdown
        className="check-variable-dropdown"
        placeholder={t("selectAction")}
        value={getDropdownValue(matchedAction, matchedPhone, selectedMatchedUser, "matched", data.onMatched)}
        renderValue={() => getDropdownDisplayValue(matchedAction, matchedPhone, selectedMatchedUser, "matched", data.onMatched)}
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        onOptionSelect={(_, data) => handleActionChange(data.optionValue, "matched")}
      >
        {ACTIONS.map(a => (
          <Option key={a.value} value={a.value}>{t(a.textKey)}</Option>
        ))}
      </Dropdown>

      {matchedAction === "queue" && (
        <QueueSelectorComponent
          displayName={selectedMatchedQueue.displayName}
          QUEUES={routingList?.queues || []}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          getData={(queue) => {
            setSelectedMatchedQueue(queue);
            onChange(routingName, "onMatched", {
              type: "queue",
              queueDisplayName: queue.displayName,
              queueId: queue.id,
            });
          }}
        />
      )}

      {(matchedAction === "transferUser" || (matchedAction === "transfer" && data.onMatched?.targetType === "user")) && (
        <SearchResourceInGraph
          displayName={selectedMatchedUser.displayName}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onSelectUser={(user) => {
            setSelectedMatchedUser(user);
            onChange(routingName, "onMatched", {
              type: "transfer",
              userDisplayName: user.displayName,
              targetId: user.id,
              targetType: "user"
            });
          }}
        />
      )}

      {matchedAction === "routing" && (
        <Dropdown
          className="check-variable-dropdown"
          placeholder={t("selectRouting")}
          value={selectedMatchedRouting}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, option) => {
            setSelectedMatchedRouting(option.optionValue);
            onChange(routingName, "onMatched", {
              type: "routing",
              routingName: option.optionValue,
            });
          }}
        >
          {Object.keys(routingList || {})
            .filter((r) => r !== routingName)
            .map((r) => (
              <Option key={r} value={r}>
                {routingList[r]?.displayName || r}
              </Option>
            ))}
        </Dropdown>
      )}

      {(matchedAction === "transferPhone" || (matchedAction === "transfer" && data.onMatched?.targetType === "phone")) && (
        <input
          type="tel"
          placeholder={t("enterPhoneNumber")}
          className={classes.input}
          value={matchedPhone}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onChange={(e) => {
            setMatchedPhone(e.target.value);
            onChange(routingName, "onMatched", { type: "transfer", targetId: e.target.value, targetType: "phone" });
          }}
        />
      )}

      {/* Unmatched */}
      <Label>{t("ifNotMatched")}</Label>
      <Dropdown
        className="check-variable-dropdown"
        placeholder={t("selectAction")}
        value={getDropdownValue(unmatchedAction, unmatchedPhone, selectedUnmatchedUser, "unmatched", data.onUnMatched)}
        renderValue={() => getDropdownDisplayValue(unmatchedAction, unmatchedPhone, selectedUnmatchedUser, "unmatched", data.onUnMatched)}
        disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
        onOptionSelect={(_, data) => handleActionChange(data.optionValue, "unmatched")}
      >
        {ACTIONS.map(a => (
          <Option key={a.value} value={a.value}>{t(a.textKey)}</Option>
        ))}
      </Dropdown>

      {unmatchedAction === "queue" && (
        <QueueSelectorComponent
          displayName={selectedUnmatchedQueue.displayName}
          QUEUES={routingList?.queues || []}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          getData={(queue) => {
            setSelectedUnmatchedQueue(queue);
            onChange(routingName, "onUnMatched", {
              type: "queue",
              queueDisplayName: queue.displayName,
              queueId: queue.id,
            });
          }}
        />
      )}

      {(unmatchedAction === "transferUser" || (unmatchedAction === "transfer" && data.onUnMatched?.targetType === "user")) && (
        <SearchResourceInGraph
          displayName={selectedUnmatchedUser.displayName}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onSelectUser={(user) => {
            setSelectedUnmatchedUser(user);
            onChange(routingName, "onUnMatched", {
              type: "transfer",
              userDisplayName: user.displayName,
              targetId: user.id,
              targetType: "user"
            });
          }}
        />
      )}

      {unmatchedAction === "routing" && (
        <Dropdown
          className="check-variable-dropdown"
          placeholder={t("selectRouting")}
          value={selectedUnmatchedRouting}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onOptionSelect={(_, option) => {
            setSelectedUnmatchedRouting(option.optionValue);
            onChange(routingName, "onUnMatched", {
              type: "routing",
              routingName: option.optionValue,
            });
          }}
        >
          {Object.keys(routingList || {})
            .filter((r) => r !== routingName)
            .map((r) => (
              <Option key={r} value={r}>
                {routingList[r]?.displayName || r}
              </Option>
            ))}
        </Dropdown>
      )}

      {(unmatchedAction === "transferPhone" || (unmatchedAction === "transfer" && data.onUnMatched?.targetType === "phone")) && (
        <input
          type="tel"
          placeholder={t("enterPhoneNumber")}
          className={classes.input}
          value={unmatchedPhone}
          disabled={isEditMode ? !permissions?.routing?.canUpdate : !permissions?.routing?.canCreate}
          onChange={(e) => {
            setUnmatchedPhone(e.target.value);
            onChange(routingName, "onUnMatched", { type: "transfer", targetId: e.target.value, targetType: "phone" });
          }}
        />
      )}
    </div>
  );
}
